/**
 * RunOutputsTab — artifacts linked to this run, or honest gap messaging.
 */
import React from 'react';
import { FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Chip } from '../../../components/ui/Chip.js';
import { type DebugPayload, findFailedStep } from './run-detail-types.js';

interface RunOutputsTabProps {
  payload: DebugPayload;
}

export const RunOutputsTab: React.FC<RunOutputsTabProps> = ({ payload }) => {
  const { run, graph, linkedEntities } = payload;
  const artifacts = linkedEntities.artifacts;
  const failedStep = findFailedStep(graph.steps);

  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden mb-4">
      {/* Card header */}
      <div className="flex items-center justify-between gap-2.5 px-4 py-3 border-b border-border bg-surface-alt">
        <span className="font-mono text-[10.5px] uppercase tracking-[1.2px] text-muted flex items-center gap-2">
          <FileText size={14} className="text-faint" />
          Outputs
        </span>
      </div>

      <div className="p-4">
        {artifacts.length > 0 ? (
          <div className="space-y-2">
            {artifacts.map((artifact) => {
              const artifactId = String(artifact['id'] ?? '');
              const artifactTitle = String(artifact['title'] ?? '—');
              const artifactKind = String(artifact['kind'] ?? '');
              return (
                <Link
                  key={artifactId}
                  to={`/artifacts/${artifactId}`}
                  className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-border bg-bg hover:bg-surface-hover transition-colors"
                >
                  <span className="text-[13px] font-medium text-heading truncate min-w-0">{artifactTitle}</span>
                  <Chip tone="neutral">{artifactKind}</Chip>
                </Link>
              );
            })}
          </div>
        ) : run.status === 'failed' ? (
          <p className="text-[12.5px] text-muted leading-[1.6]">
            No final artifact — run failed before{' '}
            {failedStep ? (
              <>
                <code className="font-mono text-[11.5px] text-danger">{failedStep.stepName}</code> could publish an
                artifact
              </>
            ) : (
              'publish-artifact'
            )}
            . Partial drafts retained in run scratch and can be recovered on re-run.
          </p>
        ) : (
          <p className="text-[12.5px] text-muted">No outputs — run produced no artifact.</p>
        )}
      </div>
    </div>
  );
};
